import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import type { ReportSummary, ReportStatus } from '@/api/types';

const POLL_INTERVAL_MS = 2500;

/** Terminal statuses — once every report tied to the assessment reaches one of these, polling
 * stops. `failed` counts as terminal (it just isn't "success") so a stuck report doesn't keep
 * the interval alive forever; `retry()` re-arms it. */
const TERMINAL_STATUSES: ReadonlySet<ReportStatus> = new Set(['preview_available', 'unlocked', 'failed']);

const isAllReady = (reports: ReportSummary[]): boolean =>
  reports.length > 0 && reports.every((report) => TERMINAL_STATUSES.has(report.status));

export interface UseSessionPollingResult {
  reports: ReportSummary[];
  loading: boolean;
  allReady: boolean;
  anyFailed: boolean;
  retry: (reportId: string) => Promise<void>;
}

/**
 * Polls `GET /reports` every 2.5s for the reports tied to `assessmentId`, stopping the interval
 * once every one of them has reached a terminal status. `retry` re-queues a failed report via
 * `POST /reports/:id/retry`, refetches immediately, and resumes the interval if it had stopped.
 */
export const useSessionPolling = (assessmentId: string): UseSessionPollingResult => {
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const { reports: all } = await api.reports.list();
      const mine = all.filter((report) => report.assessmentId === assessmentId);
      setReports(mine);
      if (isAllReady(mine)) stopPolling();
    } finally {
      setLoading(false);
    }
  }, [assessmentId, stopPolling]);

  const startPolling = useCallback(() => {
    if (timer.current) return;
    timer.current = setInterval(() => void poll(), POLL_INTERVAL_MS);
  }, [poll]);

  useEffect(() => {
    let cancelled = false;
    setReports([]);
    setLoading(true);
    stopPolling();

    void (async () => {
      await poll();
      if (!cancelled) startPolling();
    })();

    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const retry = useCallback(
    async (reportId: string) => {
      await api.reports.retry(reportId);
      await poll();
      startPolling();
    },
    [poll, startPolling],
  );

  return { reports, loading, allReady: isAllReady(reports), anyFailed: reports.some((r) => r.status === 'failed'), retry };
};
